import React, { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { Button, Card, Text, TextInput, useTheme } from "react-native-paper";
import { DrawerScreenProps } from "@react-navigation/drawer";
import { useTranslation } from "react-i18next";
import { DrawerParamList } from "../../types/navigationParams";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  addDocument,
  getCompanyDocuments,
} from "../../services/firestoreServices";
import { serverTimestamp } from "../../firebase";
import { updateTimestampAction } from "../../redux/slices/authSlice";
import { alert } from "../../utils/alert";
import { Collection } from "../../config/companies/config";

type Props = DrawerScreenProps<DrawerParamList, string>;

type ParsedDeliveryOrder = {
  sourceIndex: number;
  senderName: string;
  receiverName: string;
  streetName: string;
  houseNumber: string;
  apartmentNumber: string;
  phone: string;
  originalAddress: string;
};

type ParseResult = {
  orders: ParsedDeliveryOrder[];
  errors: string[];
};

const DELIVERY_ORDERS_COLLECTION = "delivery_orders";
const DELIVERY_STREETS_COLLECTION = "delivery_streets";
const DELIVERY_CUSTOMERS_COLLECTION = "delivery_customers";
const MANAGER_CONTACT_FIELD_NAME = "managerAlreadyCalledClient";
const MANAGER_NOT_CONTACTED = "Not Contacted";
const DEFAULT_ZONE = "Не определена";
const NUMBERED_LINE_RE = /^(\d+)\.\s*(.+)$/;
const PHONE_LINE_RE = /^(\+?\d[\d\s().,-]*)\.?$/;
const PHONE_IN_LINE_RE = /(\+?\d[\d\s().,-]{6,}\d)\.?$/;
const IGNORED_LINES = new Set(["кешегі", "кешеги"]);

const FORMAT_EXAMPLE = [
  "Формат 1:",
  "Витамин 2",
  "1. проспект Туран, 57/5 (3 подъезд 125)",
  "+7 (705) 943-05-21",
  "",
  "Формат 2:",
  "Витамин 2",
  "1. Урбигул С. +7 (700) 675-81-52",
  "Астана, улица Айнаколь, 54А, 2 подъезд 125 квартира",
].join("\n");

const normalizeKey = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

const cleanText = (value: string) =>
  value
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[,.~]\s*$/, "");

const normalizePhone = (value: string) => cleanText(value).replace(/\s+/g, " ");

const removeCityPrefix = (value: string) =>
  value.replace(/^Астана,\s*/i, "").trim();

const looksLikeAddressDetails = (value: string) =>
  /\b(подъезд|квартира|кв\.?|этаж)\b/i.test(value);

const splitReceiverAndPhone = (value: string) => {
  const phoneMatch = value.match(PHONE_IN_LINE_RE);

  if (!phoneMatch || phoneMatch.index === undefined) {
    return null;
  }

  return {
    receiverName: cleanText(value.slice(0, phoneMatch.index)),
    phone: normalizePhone(phoneMatch[1]),
  };
};

const getFieldDefault = (
  collection: Collection | undefined,
  fieldName: string,
  fallback: any,
) =>
  collection?.fields.find((field) => field.name === fieldName)?.defaultValue ??
  fallback;

const splitAddress = (address: string) => {
  const normalizedAddress = removeCityPrefix(address);
  const apartmentMatch = normalizedAddress.match(/\(([^)]*)\)\s*$/);
  const apartmentNumber = apartmentMatch ? cleanText(apartmentMatch[1]) : "";
  const addressWithoutApartment = apartmentMatch
    ? normalizedAddress.slice(0, apartmentMatch.index).trim()
    : normalizedAddress.trim();
  const commaParts = addressWithoutApartment.split(",").map(cleanText);

  if (
    !apartmentNumber &&
    commaParts.length >= 3 &&
    looksLikeAddressDetails(commaParts[commaParts.length - 1])
  ) {
    return {
      streetName: cleanText(commaParts.slice(0, -2).join(", ")),
      houseNumber: cleanText(commaParts[commaParts.length - 2]),
      apartmentNumber: cleanText(commaParts[commaParts.length - 1]),
    };
  }

  const commaIndex = addressWithoutApartment.lastIndexOf(",");

  if (commaIndex >= 0) {
    return {
      streetName: cleanText(addressWithoutApartment.slice(0, commaIndex)),
      houseNumber: cleanText(addressWithoutApartment.slice(commaIndex + 1)),
      apartmentNumber,
    };
  }

  const fallbackMatch = addressWithoutApartment.match(
    /^(.+?)\s+([0-9A-Za-zА-Яа-яӘәІіҢңҒғҚқӨөҰұҮүҺһ/.-]+)$/,
  );

  if (fallbackMatch) {
    return {
      streetName: cleanText(fallbackMatch[1]),
      houseNumber: cleanText(fallbackMatch[2]),
      apartmentNumber,
    };
  }

  return {
    streetName: cleanText(addressWithoutApartment),
    houseNumber: "",
    apartmentNumber,
  };
};

const parseDeliveryText = (input: string): ParseResult => {
  const errors: string[] = [];
  const lines = input
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !IGNORED_LINES.has(normalizeKey(line)));
  const firstNumberedIndex = lines.findIndex((line) =>
    NUMBERED_LINE_RE.test(line),
  );
  const senderName =
    firstNumberedIndex > 0
      ? cleanText(lines.slice(0, firstNumberedIndex).join(" "))
      : "";
  const dataLines =
    firstNumberedIndex >= 0 ? lines.slice(firstNumberedIndex) : lines;
  const orders: ParsedDeliveryOrder[] = [];

  for (let i = 0; i < dataLines.length; i += 1) {
    const addressMatch = dataLines[i].match(NUMBERED_LINE_RE);

    if (!addressMatch) {
      errors.push(`Строка пропущена: ${dataLines[i]}`);
      continue;
    }

    const phoneLine = dataLines[i + 1];
    const receiverAndPhone = splitReceiverAndPhone(addressMatch[2]);
    const phoneMatch = phoneLine?.match(PHONE_LINE_RE);

    if (receiverAndPhone) {
      const originalAddress = cleanText(phoneLine || "");
      const { streetName, houseNumber, apartmentNumber } =
        splitAddress(originalAddress);

      if (!phoneLine || !streetName || !houseNumber) {
        errors.push(
          `Не удалось разобрать адрес пункта ${addressMatch[1]}: ${originalAddress}`,
        );
        continue;
      }

      orders.push({
        sourceIndex: Number(addressMatch[1]),
        senderName,
        receiverName: receiverAndPhone.receiverName,
        streetName,
        houseNumber,
        apartmentNumber,
        phone: receiverAndPhone.phone,
        originalAddress,
      });

      i += 1;
      continue;
    }

    if (!phoneLine || !phoneMatch) {
      errors.push(`Не найден телефон для пункта ${addressMatch[1]}`);
      continue;
    }

    const originalAddress = cleanText(addressMatch[2]);
    const { streetName, houseNumber, apartmentNumber } =
      splitAddress(originalAddress);

    if (!streetName || !houseNumber) {
      errors.push(
        `Не удалось разобрать адрес пункта ${addressMatch[1]}: ${originalAddress}`,
      );
      i += 1;
      continue;
    }

    orders.push({
      sourceIndex: Number(addressMatch[1]),
      senderName,
      receiverName: "",
      streetName,
      houseNumber,
      apartmentNumber,
      phone: normalizePhone(phoneMatch[1]),
      originalAddress,
    });

    i += 1;
  }

  return { orders, errors };
};

export default function DeliveryParserScreen({ route }: Props) {
  const [rawText, setRawText] = useState("");
  const [saving, setSaving] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const config = useAppSelector((state) => state.auth.config);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const ordersCollection = config.database.find(
    (collection) => collection.name === DELIVERY_ORDERS_COLLECTION,
  );
  const streetsCollection = config.database.find(
    (collection) => collection.name === DELIVERY_STREETS_COLLECTION,
  );
  const customersCollection = config.database.find(
    (collection) => collection.name === DELIVERY_CUSTOMERS_COLLECTION,
  );
  const parsed = useMemo(() => parseDeliveryText(rawText), [rawText]);
  const senderName =
    parsed.orders.find((order) => order.senderName)?.senderName || "";
  const validationMessages = [
    ...(!senderName && parsed.orders.length > 0
      ? [
          "Не указан отправитель. Добавьте его первой строкой перед списком, например: Витамин 2",
        ]
      : []),
    ...parsed.errors,
  ];

  const createOrders = async () => {
    if (!ordersCollection || !streetsCollection || !customersCollection) {
      alert("Ошибка", "Не найдены настройки delivery-коллекций.");
      return;
    }

    if (parsed.orders.length === 0) {
      alert("Ошибка", "Нет заявок для создания.");
      return;
    }

    if (validationMessages.length > 0) {
      alert("Ошибка", validationMessages[0]);
      return;
    }

    setSaving(true);

    try {
      const [existingStreets, existingCustomers] = await Promise.all([
        getCompanyDocuments(DELIVERY_STREETS_COLLECTION),
        getCompanyDocuments(DELIVERY_CUSTOMERS_COLLECTION),
      ]);
      const streetByName = new Map<string, any>(
        existingStreets
          .filter((street: any) => street.name)
          .map((street: any) => [normalizeKey(street.name), street]),
      );
      const customerByPhone = new Map<string, any>(
        existingCustomers
          .filter((customer: any) => customer.phone)
          .map((customer: any) => [normalizeKey(customer.phone), customer]),
      );
      const defaultZone = getFieldDefault(
        ordersCollection,
        "deliveryZone",
        DEFAULT_ZONE,
      );

      for (const order of parsed.orders) {
        const streetKey = normalizeKey(order.streetName);
        let street = streetByName.get(streetKey);

        if (!street) {
          const streetData = {
            name: order.streetName,
            zone: getFieldDefault(streetsCollection, "zone", defaultZone),
            note: "",
            createdAt: serverTimestamp(),
          };
          const streetDoc = await addDocument(streetsCollection, streetData);
          street = { ...streetData, id: streetDoc.id };
          streetByName.set(streetKey, street);
        }

        const customerKey = normalizeKey(order.phone);
        let customer = customerByPhone.get(customerKey);

        if (!customer) {
          const customerData = {
            fullName: order.receiverName,
            phone: order.phone,
            note: "",
            createdAt: serverTimestamp(),
          };
          const customerDoc = await addDocument(
            customersCollection,
            customerData,
          );
          customer = { ...customerData, id: customerDoc.id };
          customerByPhone.set(customerKey, customer);
        }

        await addDocument(ordersCollection, {
          customerUid: user.uid,
          senderName,
          customerName: customer,
          customerPhone: order.phone,
          customerStreet: street,
          houseNumber: order.houseNumber,
          apartmentNumber: order.apartmentNumber,
          deliveryZone: street.zone || defaultZone,
          priority: getFieldDefault(ordersCollection, "priority", "normal"),
          status: getFieldDefault(ordersCollection, "status", "new"),
          courier: "",
          additionalInfo: [
            `Исходный адрес: ${order.originalAddress}`,
            order.receiverName ? `Получатель: ${order.receiverName}` : "",
            `Импорт: ${senderName || "без отправителя"} #${order.sourceIndex}`,
          ]
            .filter(Boolean)
            .join("\n"),
          [MANAGER_CONTACT_FIELD_NAME]: getFieldDefault(
            ordersCollection,
            MANAGER_CONTACT_FIELD_NAME,
            MANAGER_NOT_CONTACTED,
          ),
          customerNotificationText: "",
          undeliveredComment: "",
          archived: false,
          createdById: user.uid,
          createdBy: user,
          company: user.company,
          companyId: user.company?.id,
          createdAt: serverTimestamp(),
          index: Date.now() + order.sourceIndex,
          deleted: false,
        });
      }

      dispatch(updateTimestampAction());
      setRawText("");
      alert("Готово", `Создано заявок: ${parsed.orders.length}`);
    } catch (error) {
      console.error("Failed to import delivery orders:", error);
      alert(
        "Ошибка",
        "Не удалось создать заявки. Проверьте данные и попробуйте снова.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.content}
      bottomOffset={20}
      keyboardShouldPersistTaps="handled"
    >
      <Text variant="titleLarge">{t(route.name)}</Text>
      <TextInput
        mode="outlined"
        multiline
        numberOfLines={12}
        label="Текст заявок"
        value={rawText}
        onChangeText={setRawText}
        style={styles.input}
      />
      <Card mode="outlined">
        <Card.Content style={styles.cardContent}>
          <Text variant="titleSmall">Поддерживаемый формат</Text>
          <Text>{FORMAT_EXAMPLE}</Text>
        </Card.Content>
      </Card>
      <View style={styles.summary}>
        <Text>Будет создано заявок: {parsed.orders.length}</Text>
        {!!senderName && <Text>Отправитель: {senderName}</Text>}
      </View>
      {validationMessages.length > 0 && (
        <Card
          mode="outlined"
          style={[
            styles.validationCard,
            {
              backgroundColor: theme.colors.errorContainer,
              borderColor: theme.colors.error,
            },
          ]}
        >
          <Card.Content style={styles.cardContent}>
            <Text
              variant="titleSmall"
              style={{ color: theme.colors.onErrorContainer }}
            >
              Нужно исправить данные
            </Text>
            {validationMessages.map((message) => (
              <Text
                key={message}
                style={{ color: theme.colors.onErrorContainer }}
              >
                {message}
              </Text>
            ))}
          </Card.Content>
        </Card>
      )}
      <Button
        mode="contained"
        icon="content-save"
        loading={saving}
        disabled={
          saving || parsed.orders.length === 0 || validationMessages.length > 0
        }
        onPress={createOrders}
        style={styles.button}
      >
        Создать заявки
      </Button>
      <View style={styles.previewList}>
        {parsed.orders.map((order) => (
          <Card key={`${order.sourceIndex}-${order.phone}`} mode="outlined">
            <Card.Content style={styles.cardContent}>
              <Text variant="titleMedium">
                {order.sourceIndex}. {order.streetName}, {order.houseNumber}
              </Text>
              {!!order.apartmentNumber && <Text>{order.apartmentNumber}</Text>}
              {!!order.receiverName && <Text>{order.receiverName}</Text>}
              <Text>{order.phone}</Text>
            </Card.Content>
          </Card>
        ))}
      </View>
    </KeyboardAwareScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: 12,
    padding: 16,
    paddingBottom: 32,
  },
  input: {
    minHeight: 260,
  },
  summary: {
    gap: 4,
  },
  validationCard: {
    borderWidth: 1,
  },
  button: {
    alignSelf: "flex-start",
  },
  previewList: {
    gap: 8,
  },
  cardContent: {
    gap: 4,
  },
});
